import React, { useState, useEffect } from "react";

/**
 * ResidentialResaleForm
 * Props:
 *  - districts: array of { District: string, Division: string } (optional)
 *  - postUrl: API endpoint to submit to (optional)
 */
export default function ResidentialResaleForm({
  districts = [
    { District: "Thoothukudi", Division: "Thoothukudi Town" },
    { District: "Chennai", Division: "Anna Nagar" },
    { District: "Madurai", Division: "Thirunagar" },
  ],
  postUrl = "http://127.0.0.1:8000/api/residential/residential_resale_create/",
}) {
  const [formData, setFormData] = useState({
    BhkType: "",
    TotalFloor: "",
    PropertyAge: "",
    HouseLength: "",
    HouseWidth: "",
    HousePlotArea: "",
    HouseCent: "",
    LandLength: "",
    LandWidth: "",
    LandPlotArea: "",
    LandCent: "",
    Parking: "",
    Terrace: "",
    Hall: "",
    Bedroom: "",
    Bathroom: "",
    District: "",
    Town: "",
    Street: "",
    ExpectedPrice: "",
    Description: "",
    PrimaryNumber: "",
    SecondaryNumber: "",
  });

  const [filePreviews, setFilePreviews] = useState([]); // [{ file, url }]
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  // Utility: get csrf token cookie (Django default)
  function getCookie(name) {
    const cookieValue = document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="));
    return cookieValue ? decodeURIComponent(cookieValue.split("=")[1]) : null;
  }

  // Recalculate areas whenever lengths/widths change
  useEffect(() => {
    const hl = parseFloat(formData.HouseLength);
    const hw = parseFloat(formData.HouseWidth);
    if (!isNaN(hl) && !isNaN(hw)) {
      const area = +(hl * hw).toFixed(2);
      const cent = +((area / 435.6).toFixed(2)); // 1 cent ~= 435.6 sq.ft
      setFormData((prev) => ({
        ...prev,
        HousePlotArea: area,
        HouseCent: cent,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        HousePlotArea: "",
        HouseCent: "",
      }));
    }

    const ll = parseFloat(formData.LandLength);
    const lw = parseFloat(formData.LandWidth);
    if (!isNaN(ll) && !isNaN(lw)) {
      const area = +(ll * lw).toFixed(2);
      const cent = +((area / 435.6).toFixed(2));
      setFormData((prev) => ({
        ...prev,
        LandPlotArea: area,
        LandCent: cent,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        LandPlotArea: "",
        LandCent: "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.HouseLength, formData.HouseWidth, formData.LandLength, formData.LandWidth]);

  // Cleanup object URLs when component unmounts / files change
  useEffect(() => {
    return () => {
      filePreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [filePreviews]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    // keep numbers as strings to allow empty input and decimals
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    // revoke previous urls
    filePreviews.forEach((p) => URL.revokeObjectURL(p.url));
    const previews = files.map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setFilePreviews(previews);
  };

  const removePreview = (index) => {
    const removed = filePreviews[index];
    if (removed) URL.revokeObjectURL(removed.url);
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors(null);
    setSuccessMsg("");

    try {
      const fd = new FormData();

      // append text fields
      Object.entries(formData).forEach(([k, v]) => {
        // Avoid appending readonly computed fields if you don't want them on server:
        fd.append(k, v === undefined || v === null ? "" : v);
      });

      // append image files
      filePreviews.forEach((p) => {
        fd.append("images", p.file); // server should expect multiple 'images' fields
      });

      const csrftoken = getCookie("csrftoken");

      const resp = await fetch(postUrl, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: csrftoken ? { "X-CSRFToken": csrftoken } : undefined,
      });

      if (!resp.ok) {
        // try to parse JSON errors
        let data = null;
        try {
          data = await resp.json();
        } catch (err) {
          // plain text error
          throw new Error(`Server returned ${resp.status}`);
        }
        setErrors(data);
        setSubmitting(false);
        return;
      }

      const data = await resp.json();
      setSuccessMsg(data.message || "Form submitted successfully!");
      // optionally clear form:
      // setFormData({...initial values...})
      setSubmitting(false);
    } catch (err) {
      setErrors({ non_field_errors: [err.message] });
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <form
        className="form"
        id="residentialResaleForm"
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        <h3 className="alert alert-warning">Residential Resale</h3>

        {/* Property Details Start */}
        <h4 className="alert alert-primary">Property Details</h4>
        <h6 className="alert alert-info">House</h6>
        <hr />

        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="BhkType" className="form-label">
                BHK Type <span className="text-danger">*</span>
              </label>
              <select
                name="BhkType"
                id="BhkType"
                className="form-control"
                value={formData.BhkType}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select
                </option>
                <option value="1 RK">1 RK</option>
                <option value="1 BHK">1 BHK</option>
                <option value="2 BHK">2 BHK</option>
                <option value="3 BHK">3 BHK</option>
                <option value="4 BH">4 BHK</option>
                <option value="4+ BH">4+ BHK</option>
              </select>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="TotalFloor" className="form-label">
                Total Floor<span className="text-danger">*</span>
              </label>
              <select
                name="TotalFloor"
                id="TotalFloor"
                className="form-control"
                value={formData.TotalFloor}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select
                </option>
                <option value="No Floor">No Floor</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4+</option>
              </select>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="PropertyAge" className="form-label">
                Property Age<span className="text-danger">*</span>
              </label>
              <select
                name="PropertyAge"
                id="PropertyAge"
                className="form-control"
                value={formData.PropertyAge}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select
                </option>
                <option value="Less than a Year">Less than a Year</option>
                <option value="1 to 3 Years">1 to 3 Years</option>
                <option value="3 to 5 Years">3 to 5 Years</option>
                <option value="5 to 10 Years">5 to 10 Years</option>
                <option value="More than 10 Years">More than 10 Years</option>
              </select>
            </div>
          </div>
        </div>

        {/* House Dimensions */}
        <div className="row mt-3">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="HouseLength" className="form-label">
                House Length<span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type="number"
                  name="HouseLength"
                  id="HouseLength"
                  className="form-control"
                  placeholder="Length"
                  min="0"
                  step="0.01"
                  value={formData.HouseLength}
                  onChange={handleNumberChange}
                />
                <span className="input-group-text">ft.</span>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="HouseWidth" className="form-label">
                House Width<span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type="number"
                  name="HouseWidth"
                  id="HouseWidth"
                  className="form-control"
                  placeholder="Width"
                  min="0"
                  step="0.01"
                  value={formData.HouseWidth}
                  onChange={handleNumberChange}
                />
                <span className="input-group-text">ft.</span>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="HousePlotArea" className="form-label">
                Plot Area
              </label>
              <div className="input-group mb-3">
                <input
                  type="number"
                  className="form-control"
                  id="HousePlotArea"
                  name="HousePlotArea"
                  placeholder="Plot Area"
                  readOnly
                  value={formData.HousePlotArea || ""}
                />
                <div className="input-group-append">
                  <span className="input-group-text" id="plot-area-addon">
                    sq.ft
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="HouseCent" className="form-label">
                Cent
              </label>
              <div className="input-group mb-3">
                <input
                  type="number"
                  className="form-control"
                  id="HouseCent"
                  name="HouseCent"
                  placeholder="Cent"
                  readOnly
                  value={formData.HouseCent || ""}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Land */}
        <h6 className="alert alert-info mt-3">Land</h6>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="LandLength" className="form-label">
                Land Length<span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type="number"
                  name="LandLength"
                  id="LandLength"
                  className="form-control"
                  placeholder="Length"
                  min="0"
                  step="0.01"
                  value={formData.LandLength}
                  onChange={handleNumberChange}
                />
                <span className="input-group-text">ft.</span>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="LandWidth" className="form-label">
                Land Width<span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type="number"
                  name="LandWidth"
                  id="LandWidth"
                  className="form-control"
                  placeholder="Width"
                  min="0"
                  step="0.01"
                  value={formData.LandWidth}
                  onChange={handleNumberChange}
                />
                <span className="input-group-text">ft.</span>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="LandPlotArea" className="form-label">
                Plot Area
              </label>
              <div className="input-group mb-3">
                <input
                  type="number"
                  className="form-control"
                  id="LandPlotArea"
                  name="LandPlotArea"
                  placeholder="Plot Area"
                  readOnly
                  value={formData.LandPlotArea || ""}
                />
                <div className="input-group-append">
                  <span className="input-group-text" id="plot-area-addon">
                    sq.ft
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="LandCent" className="form-label">
                Cent
              </label>
              <div className="input-group mb-3">
                <input
                  type="number"
                  className="form-control"
                  id="LandCent"
                  name="LandCent"
                  placeholder="Cent"
                  readOnly
                  value={formData.LandCent || ""}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Amenities */}
        <h6 className="alert alert-info mt-3">Amenities</h6>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="Parking" className="form-label">
                Parking<span className="text-danger">*</span>
              </label>
              <select
                name="Parking"
                id="Parking"
                className="form-control"
                value={formData.Parking}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  --Select--
                </option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="Terrace" className="form-label">
                Terrace<span className="text-danger">*</span>
              </label>
              <select
                name="Terrace"
                id="Terrace"
                className="form-control"
                value={formData.Terrace}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  --Select--
                </option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="Hall" className="form-label">
                Hall<span className="text-danger">*</span>
              </label>
              <select
                name="Hall"
                id="Hall"
                className="form-control"
                value={formData.Hall}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  --Select--
                </option>
                <option value="No Hall">No Hall</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3+</option>
              </select>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="Bedroom" className="form-label">
                BedRoom<span className="text-danger">*</span>
              </label>
              <select
                name="Bedroom"
                id="Bedroom"
                className="form-control"
                value={formData.Bedroom}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  --Select--
                </option>
                <option value="No Hall">No Bedroom</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3+</option>
              </select>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="Bathroom" className="form-label">
                BathRoom<span className="text-danger">*</span>
              </label>
              <select
                name="Bathroom"
                id="Bathroom"
                className="form-control"
                value={formData.Bathroom}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  --Select--
                </option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3+</option>
              </select>
            </div>
          </div>
        </div>

        <hr />

        {/* Locality Start */}
        <h4 className="alert alert-primary">Locality Details</h4>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="District" className="form-label">
                District <span className="text-danger">*</span>
              </label>
              <select
                name="District"
                id="District"
                className="form-control"
                value={formData.District}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select
                </option>
                {districts.map((i, idx) => (
                  <option key={idx} value={i.District}>
                    {i.District}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-6">
            <div className="form-group">
              <label htmlFor="Town" className="form-label">
                Town <span className="text-danger">*</span>
              </label>
              <select
                name="Town"
                id="Town"
                className="form-control"
                value={formData.Town}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  --Choose--
                </option>
                {districts.map((i, idx) => (
                  <option key={idx} value={i.Division}>
                    {i.Division}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="Street" className="form-label">
                Landmark / Street <span className="text-danger">*</span>
              </label>
              <input
                type="search"
                name="Street"
                id="Street"
                className="form-control"
                placeholder="E.g. KVK Nagar"
                value={formData.Street}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        <hr />

        {/* Resale Details */}
        <h4 className="alert alert-primary">Resale Details</h4>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="ExpectedPrice" className="form-label">
                Expected Price <span className="text-danger">*</span>
              </label>
              <div className="input-group mb-3">
                <span className="input-group-text">₹</span>
                <input
                  type="number"
                  name="ExpectedPrice"
                  className="form-control"
                  id="ExpectedPrice"
                  placeholder="Enter Amount"
                  min="1"
                  value={formData.ExpectedPrice}
                  onChange={handleNumberChange}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-md-12">
            <div className="form-group">
              <label htmlFor="Description" className="form-label">
                Description
              </label>
              <textarea
                name="Description"
                id="Description"
                cols="30"
                rows="3"
                className="form-control"
                value={formData.Description}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <hr />

        {/* Upload Images */}
        <h4 className="alert alert-primary">Upload Images</h4>
        <div className="input-group mb-3">
          <input
            type="file"
            className="form-control"
            name="images"
            id="imageInput"
            multiple
            accept="image/*"
            onChange={handleFilesChange}
          />
          <label className="input-group-text btn btn-primary" htmlFor="imageInput">
            Upload
          </label>
        </div>

        <div className="mt-2 d-flex flex-wrap">
          {filePreviews.map((p, idx) => (
            <div key={idx} className="me-2 mb-2 position-relative" style={{ width: 110 }}>
              <img
                src={p.url}
                alt={`preview-${idx}`}
                style={{ width: 110, height: 80, objectFit: "cover", borderRadius: 6 }}
                className="border"
              />
              <button
                type="button"
                onClick={() => removePreview(idx)}
                className="btn btn-sm btn-danger position-absolute"
                style={{ top: -8, right: -8 }}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <hr />

        {/* Contact Details */}
        <h4 className="alert alert-primary">Contact Details</h4>

        <div className="row mt-2">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="PrimaryNumber" className="form-label">
                Primary Number<span className="text-danger">*</span>
              </label>
              <input
                type="tel"
                name="PrimaryNumber"
                id="PrimaryNumber"
                className="form-control"
                placeholder="Enter Mobile Number"
                pattern="[0-9]{10}"
                value={formData.PrimaryNumber}
                onChange={handleNumberChange}
                required
              />
            </div>
          </div>

          <div className="col-6">
            <div className="form-group">
              <label htmlFor="SecondaryNumber" className="form-label">
                Secondary Number<span className="text-danger">*</span>
              </label>
              <input
                type="tel"
                name="SecondaryNumber"
                id="SecondaryNumber"
                className="form-control"
                placeholder="Enter Secondary Mobile Number"
                pattern="[0-9]{10}"
                value={formData.SecondaryNumber}
                onChange={handleNumberChange}
              />
            </div>
          </div>
        </div>

        <br />
        <hr />

        {errors && (
          <div className="alert alert-danger">
            <strong>Errors:</strong>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{JSON.stringify(errors, null, 2)}</pre>
          </div>
        )}

        {successMsg && <div className="alert alert-success">{successMsg}</div>}

        <div className="row mt-3">
          <div className="col-md-12">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
